import React from "react";
import { bool, func } from "prop-types";
import { useTranslation } from "@lib/i18n";

import PackageDetailDescription from "./PackageDetailDescription";
import Typography from "@components/shared/Typography";
import RequestButton from "@components/shared/RequestButton";
import FinancePrice from "@components/shared/FinancePrice";
import ScrollableContainer from "@components/shared/ScrollableContainer";

import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { getIsCountry, COUNTRIES } from "@lib/getIsCountry";
import { personalizeOptionType } from "@types";
import { useIsCashJourneySelected } from "@hooks/useIsCashJourneySelected";
import { useIsLOAJourneySelected } from "@hooks/useIsLOAJourneySelected";
import { useIsVACJourneySelected } from "hooks/useIsVACJourneySelected";

import { useAccessoryIncrease } from "@hooks/finance-data/useAccessoryIncrease";
import { useGetFormattedPrice } from "@hooks/useGetFormattedPrice";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useGetJourneySpecificConfig } from "@hooks/useGetJourneySpecificConfig";
import { priceAlgorithm } from "@lib/priceAlgorithm";
import { useVehicleQuantitySelector } from "@redux/reducers/deal";

import { hyphenate } from "@components/shared/utils";

const PackageDetail = ({
  changeConfiguration,
  isSelected,
  option,
  withGallery,
  readOnly,
}) => {
  const { personalizationOptions, netPrice, label, description, id } = option;
  const financeAccessoryIncrease = useAccessoryIncrease(id);
  const { t } = useTranslation();
  const isCashJourneySelected = useIsCashJourneySelected();
  const isLOAJourneySelected = useIsLOAJourneySelected();
  const isVACJourneySelected = useIsVACJourneySelected();
  const isB2BSelected = useIsB2BSelected();
  const { multipleVehicles } = useGetJourneySpecificConfig();
  const getFormattedPrice = useGetFormattedPrice();
  const quantity = useVehicleQuantitySelector();
  const isMultipleVehiclesAllowed = multipleVehicles?.enabled;
  const isSinglePaymentPriceFormat =
    isCashJourneySelected ||
    isLOAJourneySelected ||
    (getIsCountry(COUNTRIES.BE) && isVACJourneySelected);

  const price = priceAlgorithm.times(netPrice, quantity);

  return (
    <div className="w-full pb-6">
      {!readOnly && (
        <div className="flex flex-col gap-4 justify-between items-center pb-8 relative text-black smallMobile:flex-row">
          <RequestButton
            data-id={`customize-${isSelected ? "remove" : "add"}-package`}
            triggerRequestCallback={() => changeConfiguration(option)}
            variant={isSelected ? "secondary" : "primary"}
          >
            {isSelected ? t("customize.remove") : t("customize.add")}
          </RequestButton>
          <Typography
            className="text-4xl uppercase font-semibold"
            data-id={`price-package-detail-${label}`}
          >
            {isSinglePaymentPriceFormat ? (
              <>
                {price >= 0 && (
                  <span className="px.1">{`${t("mathSymbols.plus")}`}</span>
                )}
                {`${getFormattedPrice(price)}`}
              </>
            ) : (
              <>
                {financeAccessoryIncrease && ""}
                {isMultipleVehiclesAllowed && (
                  <>{t("mathSymbols.times", { quantity })} </>
                )}
                {financeAccessoryIncrease && financeAccessoryIncrease >= 0 && (
                  <span className="px.1">{t("mathSymbols.plus")}</span>
                )}
                <FinancePrice amount={financeAccessoryIncrease} />
                {financeAccessoryIncrease && (
                  <sup className="text-sm pl-1">
                    {isB2BSelected
                      ? t("price.b2b.month")
                      : t("price.b2c.month")}
                  </sup>
                )}
              </>
            )}
          </Typography>
        </div>
      )}
      <div data-id="customize-include">
        <div className="flex flex-col mb-4">
          <Typography
            className="uppercase font-semibold my-2"
            data-id={`customize-include-item-name-${hyphenate(label)}`}
          >
            {label}
          </Typography>
          <PackageDetailDescription description={description} />
        </div>
        {withGallery && (
          <ScrollableContainer>
            {personalizationOptions.map(({ id, label }) => (
              <figure
                className="max-w-30"
                data-id={`customize-image-${label}`}
                key={id}
              >
                <img
                  alt={label}
                  className="size-30 object-cover rounded-sm"
                  src={getStaticAssetURL(
                    `/images/gallery/personalization/${id}.jpg`
                  )}
                  title={label}
                />
                <figcaption className="text-black text-sm mt-3 whitespace-normal">
                  {label}
                </figcaption>
              </figure>
            ))}
          </ScrollableContainer>
        )}
      </div>
    </div>
  );
};

PackageDetail.propTypes = {
  changeConfiguration: func.isRequired,
  isSelected: bool.isRequired,
  option: personalizeOptionType.isRequired,
  withGallery: bool,
  readOnly: bool,
};

export default PackageDetail;
